import React, { useEffect, useState, FunctionComponent } from 'react'

import { ConnectedBuyFormatCurrency } from '@buy/connectedComponents'
import { Tooltip } from '@common/components'
import { OrderSummaryLine } from '@buy/state/order-summary/order-summary'
import { isEmpty } from 'lodash-es'
import { BillingPeriod } from '@common/constants'
import styles from '../../connectedComponents/OrderSummary/order-summary.module.css'
import { BillingTenureValue } from '../BillingPeriodSwitch'

interface TotalSummaryProps {
    subTotalLabel: string
    estimatedTaxLabel: string
    estimatedTaxPopupInfoLabel: string
    tBDLabel: string
    totalLabel: string
    calculatingLabel: string
    annualPlanNoteLabel: string
    monthlyPlanNoteLabel: string
    annualContractPlanNoteLabel: string
    annualContractTotalLabel: string
    totalLoading: boolean
    calculatedTax: number
    currencyCode: string
    baseLineTotal: number
    additionalLinesTotal: Record<string, number>
    addonLinesTotal: number
    totalDiscount: number
    additionalLines: Array<OrderSummaryLine>
    setFinalTotal: (total: number) => void
    finalTotal: number
    billingTenure: BillingTenureValue
}

export const TotalSummary: FunctionComponent<TotalSummaryProps> = ({
    subTotalLabel,
    estimatedTaxLabel,
    estimatedTaxPopupInfoLabel,
    tBDLabel,
    totalLabel,
    calculatingLabel,
    annualPlanNoteLabel,
    monthlyPlanNoteLabel,
    totalLoading,
    calculatedTax,
    currencyCode,
    baseLineTotal,
    additionalLinesTotal,
    addonLinesTotal,
    totalDiscount,
    additionalLines,
    finalTotal,
    setFinalTotal,
    annualContractPlanNoteLabel,
    annualContractTotalLabel,
    billingTenure,
}) => {
    const noteLabels = {
        [BillingPeriod.ANNUAL]: annualPlanNoteLabel,
        [BillingPeriod.ANNUALBILLEDMONTHLY]: annualContractPlanNoteLabel,
        [BillingPeriod.MONTHLY]: monthlyPlanNoteLabel,
    }

    const totalLabels = {
        [BillingPeriod.ANNUAL]: totalLabel,
        [BillingPeriod.ANNUALBILLEDMONTHLY]: annualContractTotalLabel,
        [BillingPeriod.MONTHLY]: totalLabel,
    }

    const [subTotal, setSubTotal] = useState(0)

    useEffect(() => {
        const additionalLinesSum = Object.keys(additionalLinesTotal).reduce((sum, key) => {
            const additionLineTotal = !isEmpty(additionalLines.find((item) => item.unit! === key))
                ? additionalLinesTotal[key] || 0
                : 0
            return sum + additionLineTotal
        }, 0)
        setSubTotal(baseLineTotal + additionalLinesSum + addonLinesTotal - totalDiscount)
    }, [baseLineTotal, additionalLinesTotal, addonLinesTotal, totalDiscount, additionalLines])

    useEffect(() => {
        setFinalTotal(subTotal + calculatedTax)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subTotal, calculatedTax])

    return (
        <>
            <div className={`${styles.section} ${styles.total}`} data-qat="totals-summary">
                <div className={`${styles.row}  ${styles['row--subtotal']}`} data-qat="order-subtotal">
                    <span data-qat="label">{subTotalLabel}</span>
                    <span data-qat="amount">
                        {totalLoading ? '--' : <ConnectedBuyFormatCurrency value={subTotal} />}
                    </span>
                </div>
                <div className={`${styles.row}  ${styles['row--subtotal']}`} data-qat="order-estimated-tax">
                    <span data-qat="label">
                        {estimatedTaxLabel}{' '}
                        <Tooltip
                            content={estimatedTaxPopupInfoLabel}
                            additionalClass={styles['order-tooltip']}
                            contentClass={styles['order-tooltip-content']}
                        />
                    </span>

                    <span data-qat="amount">
                        {totalLoading ? (
                            '--'
                        ) : (
                            <>{calculatedTax ? <ConnectedBuyFormatCurrency value={calculatedTax} /> : tBDLabel}</>
                        )}
                    </span>
                </div>
                <div className={`${styles.row} ${styles['row--total']}`} data-qat="order-total">
                    <span data-qat="label">
                        {totalLabels[billingTenure.selected]} {currencyCode}
                    </span>
                    <span data-qat="amount">
                        {totalLoading ? (
                            `${calculatingLabel}...`
                        ) : (
                            <ConnectedBuyFormatCurrency value={finalTotal > 0 ? finalTotal : 0} />
                        )}
                    </span>
                </div>
                <div className={`${styles.row} ${styles['row--total-note']}`} data-qat="subscription-applied-days">
                    <span>{noteLabels[billingTenure.selected]}</span>
                </div>
            </div>
        </>
    )
}
